import { useTranslation } from 'react-i18next';
import { RuBadges } from './RuBadges';
import { Logo, priceLabel } from './ToolCard';
import type { ToolCard as ToolCardData } from '@/utils/api-pb';

interface CompactToolCardProps {
  tool: ToolCardData;
  /** «Почему вам» — only the first card of a recipe path gets one, when it arrived. */
  why?: string;
  whyLoading?: boolean;
  onGo: (slug: string) => void;
}

/**
 * Compact card for recipe mode (F-08.1): logo, name, RU badges, price and the CTA in a
 * narrow fixed-width tile laid out in a horizontal scroll row per role. Match percentage
 * is deliberately absent — in a recipe the role answers "why these", not a score.
 */
export function CompactToolCard({ tool, why, whyLoading, onGo }: CompactToolCardProps) {
  const { t } = useTranslation('tools');

  return (
    <article
      className="flex w-[240px] shrink-0 flex-col gap-2 rounded-[20px] border border-[var(--grey-accent,#eeeeee)] bg-white p-4"
      data-slug={tool.slug}
    >
      <div className="flex items-center gap-2.5">
        <Logo src={tool.logoUrl} name={tool.name} size={36} />
        {/* h5, not h4: the role title above is an h4 — the card must nest inside it. */}
        <h5 className="min-w-0 flex-1 truncate font-manrope text-[15px] font-semibold text-[color:var(--black-text,#0d0d0d)]">
          {tool.name}
        </h5>
      </div>

      <p className="line-clamp-1 font-segoe text-[12px] text-[color:var(--grey-text,#565858)]">{priceLabel(tool, t)}</p>
      <RuBadges tool={tool} />

      {whyLoading && <div className="h-4 w-3/4 animate-pulse rounded bg-[var(--grey-background,#f8f8f8)]" aria-hidden />}
      {why && (
        <p className="font-segoe text-[13px] text-[color:var(--black-text,#0d0d0d)]">
          <span className="font-semibold">{t('why')}</span> {why}
        </p>
      )}

      <button
        type="button"
        onClick={() => onGo(tool.slug)}
        className="mt-auto h-9 rounded-[12px] bg-[#2197F1] px-4 font-segoe text-[14px] font-medium text-white transition-opacity hover:opacity-90"
      >
        {t('go')} →
      </button>
    </article>
  );
}
